import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { FileAsset, ImageAsset } from '@rive-app/react-canvas';
import { Fit, Layout } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { loadRiveImage } from '../../../features/rewards/daily/dailyBonusV2/dailyBonusRiveHelpers';
import { getStampCardBodyCopy } from '../../../features/widgets/components/StampCardWidget/getStampCardBodyCopy';
import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { analytics } from '../../../services/analytics';
import { isStampLockedByTime, type StampCardStamp } from '../../../stores/stamp-card/types';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';
import { formatTime } from '../../../utils/time';

import styles from './StampCardDialog.module.scss';
import { toPrizeLines } from './stampCardPrizes';

const MAX_STAMPS = 15;

const MAX_PRIZES = 5;

function getStampEnumValue(stamp: StampCardStamp | undefined): 'LOCKED' | 'EMPTY' | 'STAMPED' | 'FREE_STAMP' {
  if (!stamp) return 'EMPTY';
  if (isStampLockedByTime(stamp)) return 'LOCKED';
  if (stamp.isFree) return 'FREE_STAMP';
  if (stamp.status === 'LOCKED' || stamp.status === 'AVAILABLE') return 'EMPTY';
  return 'STAMPED';
}

function StampCardDialogComponent() {
  const { portalStore, stampCardStore } = useStores();
  const { t, i18n } = useTranslation();
  const { currentStampCard } = stampCardStore;

  // Let the store's own `beforeClose` run (see `openStampCardDialog`): it resets
  // `isStampCardDialogOpen` *and* calls `onDialogFinished`. Suppressing it left that flag stuck
  // true, which gates both paths that re-queue this dialog — the socket handler and `fetch` — so
  // the pop-up would never auto-open again for the rest of the session.
  const close = useCallback(() => {
    portalStore.closePortal();
  }, [portalStore]);

  // Fires once per open, whichever surface opened it. Progress rides the type/value pair rather
  // than a property column, per the spec.
  const hasSentImpression = useRef(false);
  useEffect(() => {
    if (!currentStampCard || hasSentImpression.current) return;
    hasSentImpression.current = true;

    const stampsCollected = currentStampCard.stamps.filter((stamp) => stamp.status === 'COMPLETED').length;
    void analytics.sendStampCardEvent('popup', {
      event_parent: 'features',
      event_category: 'stamp_card',
      event_action: 'impression',
      event_type: 'stamps_collected',
      event_value: String(stampsCollected),
    });
  }, [currentStampCard]);

  const grandPrizeRewardPackage = currentStampCard?.stamps.find(
    (stamp) => stamp.stampIndex === currentStampCard.stampCount,
  )?.rewardPackage;

  const referencedAssets = useMemo(() => {
    const assets: Record<string, string> = {};
    toPrizeLines(grandPrizeRewardPackage, t)
      .slice(0, MAX_PRIZES)
      .forEach((item, index) => {
        if (item.imageUrl) assets[`placeholder_${index}`] = item.imageUrl;
      });
    return assets;
  }, [grandPrizeRewardPackage, t]);

  const assetLoader = useCallback(
    (asset: FileAsset, _bytes: Uint8Array) => {
      const url = asset.isImage && referencedAssets[asset.name];
      if (!url) return false;
      void loadRiveImage(url, asset as ImageAsset);
      return true;
    },
    [referencedAssets],
  );

  const {
    rive,
    RiveComponent,
    fireRiveViewModelTrigger,
    onRiveViewModelTrigger,
    setRiveViewModelEnum,
    setRiveViewModelBoolean,
  } = useRiveMachine({
    src: currentStampCard?.assets.popupLayoutUrl ?? undefined,
    artboard: 'Main',
    autoBind: true,
    stateMachineName: 'State Machine 1',
    assetLoader,
    onLoadError: (error) => {
      devError('Stamp card Rive load error', error);
    },
    layout: new Layout({
      fit: Fit.Contain,
    }),
  });

  const previousStampValuesRef = useRef<Record<number, string>>({});
  const activatedCardRef = useRef<string | null>(null);
  const hydratedCardRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rive || !currentStampCard) return;

    const vm = rive.viewModelInstance;
    if (!vm) return;

    const numOfStamps = vm.number?.('numOfStamps');
    if (numOfStamps) numOfStamps.value = currentStampCard.stampCount;

    const grandPrizeStamp = currentStampCard.stamps.find((stamp) => stamp.stampIndex === currentStampCard.stampCount);
    const rewardItems = toPrizeLines(grandPrizeStamp?.rewardPackage, t).slice(0, MAX_PRIZES);

    const numOfPrizes = vm.number?.('numOfPrizes');
    if (numOfPrizes) numOfPrizes.value = rewardItems.length;

    for (let prizeIndex = 1; prizeIndex <= MAX_PRIZES; prizeIndex++) {
      const item = rewardItems[prizeIndex - 1];
      if (!item) continue;

      setRiveViewModelEnum(`prizes/prize${prizeIndex}/prizeType`, item.prizeType);

      const coinsAmount = vm.string?.(`prizes/prize${prizeIndex}/coinsAmount`);
      if (coinsAmount) coinsAmount.value = item.coinsAmount;

      const placeholderNum = vm.number?.(`prizes/prize${prizeIndex}/placeholderNum`);
      if (placeholderNum && item.rawAmount != null) placeholderNum.value = item.rawAmount;
    }

    for (let stampIndex = 1; stampIndex <= MAX_STAMPS; stampIndex++) {
      const stamp = currentStampCard.stamps.find((s) => s.stampIndex === stampIndex);
      const isCompleted = Boolean(stamp?.completedAt);
      const nextValue = getStampEnumValue(stamp);
      const nextKey = `${nextValue}:${isCompleted ? 'done' : 'pending'}`;
      const previousKey = previousStampValuesRef.current[stampIndex];

      setRiveViewModelEnum(`stampsLayout/stamp${stampIndex}/stampState`, nextValue);

      const isLiveTransition = previousKey !== undefined && previousKey !== nextKey;
      fireRiveViewModelTrigger(`stampsLayout/stamp${stampIndex}/changeStamp`);
      if (isCompleted && stamp?.isFree && (isLiveTransition || stamp.freeStampAnimationShown)) {
        fireRiveViewModelTrigger(`stampsLayout/stamp${stampIndex}/stamped`);
      }
      previousStampValuesRef.current[stampIndex] = nextKey;
    }

    hydratedCardRef.current = currentStampCard.userStampCardId;
  }, [rive, currentStampCard, setRiveViewModelEnum, fireRiveViewModelTrigger, t]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;

    setRiveViewModelBoolean('claimVisible', true);
    const claimText = vm?.string?.('claimBTN/text');
    const isCanClaim = !!currentStampCard?.completedAt;

    if (!claimText) return;

    if (isCanClaim) {
      claimText.value = t('common.buttons.claim');
    } else {
      claimText.value = t('common.buttons.awesome');
    }
  }, [rive, currentStampCard?.completedAt, setRiveViewModelBoolean, t]);

  useEffect(() => {
    const bodyText = rive?.viewModelInstance?.string?.('bodyText');
    if (!bodyText || !currentStampCard) return;

    bodyText.value = getStampCardBodyCopy(currentStampCard, i18n.language, t);
  }, [rive, currentStampCard, i18n.language, t]);

  useEffect(() => {
    const isCanClaim = !!currentStampCard?.completedAt;
    setRiveViewModelBoolean('isCanClaim', isCanClaim);
    setRiveViewModelBoolean('xVisible', !isCanClaim);
  }, [rive, currentStampCard?.completedAt, setRiveViewModelBoolean]);

  useEffect(() => {
    const userStampCardId = currentStampCard?.userStampCardId;
    if (!userStampCardId) return undefined;

    return onRiveViewModelTrigger('claimBTN/click', () => {
      if (currentStampCard?.completedAt) {
        void stampCardStore.claimReward(userStampCardId);
      } else {
        void analytics.sendStampCardEvent('popup_close', {
          event_parent: 'features',
          event_category: 'stamp_card',
          event_action: 'click',
          button_text: 'awesome',
        });
        close();
      }
    });
  }, [currentStampCard?.userStampCardId, currentStampCard?.completedAt, onRiveViewModelTrigger, stampCardStore, close]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    const timerProp = vm?.string?.('timer');
    const expiresAt = currentStampCard?.expiresAt;
    if (!timerProp || !expiresAt) return;

    const updateCountdown = () => {
      const secondsLeft = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      timerProp.value = formatTime(secondsLeft, 's', t);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [rive, currentStampCard?.expiresAt, t]);

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm || !currentStampCard) return undefined;

    const updateLockTimers = () => {
      for (let stampIndex = 1; stampIndex <= MAX_STAMPS; stampIndex++) {
        const stamp = currentStampCard.stamps.find((s) => s.stampIndex === stampIndex);
        if (!stamp?.unlocksAt) continue;

        const lockTimerProp = vm.string?.(`stampsLayout/stamp${stampIndex}/lockTimer`);
        if (!lockTimerProp) continue;

        const secondsLeft = Math.max(0, Math.floor((new Date(stamp.unlocksAt).getTime() - Date.now()) / 1000));
        lockTimerProp.value = formatTime(secondsLeft, 's', t);

        if (secondsLeft === 0) {
          const unlockedStampAnimProp = vm.boolean?.(`stampsLayout/stamp${stampIndex}/unlockedStampAnim`);
          if (unlockedStampAnimProp) unlockedStampAnimProp.value = true;
        }
      }
    };

    updateLockTimers();
    const interval = setInterval(updateLockTimers, 1000);
    return () => clearInterval(interval);
  }, [rive, currentStampCard, t]);

  useEffect(() => {
    const userStampCardId = currentStampCard?.userStampCardId;
    if (!rive || !userStampCardId || activatedCardRef.current === userStampCardId) return;
    if (hydratedCardRef.current !== userStampCardId) return;

    if (fireRiveViewModelTrigger('activate')) {
      activatedCardRef.current = userStampCardId;
    }
  }, [rive, currentStampCard, fireRiveViewModelTrigger]);

  useEffect(() => onRiveViewModelTrigger('finish', close), [onRiveViewModelTrigger, close]);

  return (
    <div className={styles.container}>
      <RiveComponent className={styles.rive} />
    </div>
  );
}

export const StampCardDialog = observer(StampCardDialogComponent);
